import React, { useMemo } from "react";
import { Alert, Chip, Typography } from "@mui/material";

import { CITY_PLANNER_AREA_MAX } from "fond/constants";
import { usePermissionCheck } from "fond/hooks/usePermissionCheck";
import { EditMode, setEditMode } from "fond/project/redux";
import { MultiProject } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { formatNumber } from "fond/utils/number";
import { Actions } from "fond/utils/permissions";
import { StackedNavigationButton, SvgIcon, useStackedNavigationContext } from "fond/widgets";

import { CITY_PLANNER_SCREENS } from "../constants";

interface AreaPanelProps {
  multiProject: MultiProject;
}

const AreaPanel: React.FC<AreaPanelProps> = ({ multiProject }: AreaPanelProps) => {
  const dispatch = useAppDispatch();
  const hasProjects = useMemo(() => multiProject?.Areas.some((area) => area.Project?.ID), [multiProject]);
  const { open } = useStackedNavigationContext();
  const canEditMultiProject = usePermissionCheck(Actions.CITY_PLANNER_EDIT, multiProject?.Permission.Level);

  const handleOnClick = async () => {
    await dispatch(setEditMode(EditMode.none)); // ensure any existing edit mode is cleared
    open(CITY_PLANNER_SCREENS.AREA_DRAW);
    await dispatch(setEditMode(EditMode.edit));
  };

  return (
    <>
      <Typography variant="body1" sx={{ fontSize: (theme) => theme.typography.size.small, fontWeight: 500 }}>
        Define your subareas
      </Typography>
      <Typography sx={{ fontSize: (theme) => theme.typography.size.small }}>Subareas are projects that will generate a plan.</Typography>
      <br />
      <Typography component="p" variant="caption" color="biarri.secondary.grey">
        Note that there is a {formatNumber(CITY_PLANNER_AREA_MAX)} prem limit per subarea.
      </Typography>
      <StackedNavigationButton
        data-testid="area-method-navigation"
        icon={<SvgIcon icon="polygon" />}
        title="Identify subareas"
        onClick={handleOnClick}
        disabled={hasProjects || !canEditMultiProject}
        rightElement={
          multiProject.Areas.length > 0 ? (
            <Chip
              size="small"
              label={
                <Typography variant="body3" fontWeight={400}>
                  {multiProject.Areas.length}
                </Typography>
              }
              sx={{ mr: 1 }}
            />
          ) : null
        }
        sx={{ m: 0, mt: 1 }}
      />

      {hasProjects && (
        <Alert severity="info" icon={false} sx={{ mt: 1, fontSize: (theme) => theme.typography.size.small }}>
          <b>Note:</b> the subareas cannot be edited once projects have been created.
        </Alert>
      )}
    </>
  );
};

export default AreaPanel;
